import { z } from 'zod';
import { captchaTokenSchema } from './captcha.js';
import { ianaTimezoneSchema } from './location.js';
import { passwordSchema } from './password.js';
import { e164Schema } from './phone.js';
import { tenantSlugSchema } from './tenant.js';
import { label } from './text-policy.js';
/**
 * The self-serve signup contract (S26 Phase 3: a stranger creates a tenant and an owner
 * account for it, both PENDING until a verification code is redeemed).
 */
/** The channels a signup verification code can travel on. Mirrors the Prisma
 *  `VerificationChannel` enum in packages/db; the two must change together. */
export const VERIFICATION_CHANNELS = ['EMAIL', 'SMS'];
export const verificationChannelSchema = z.enum(VERIFICATION_CHANNELS);
/**
 * `phone` IS OPTIONAL IN THE SCHEMA AND CONDITIONALLY REQUIRED IN THE SERVICE, and that
 * split is deliberate: whether a phone is required is `env.SIGNUP_PHONE_VERIFICATION`,
 * and `@instanexus/contracts` is dependency-free and browser-safe — it cannot read the
 * API's environment, and a contract that hard-coded `required` would be wrong in every
 * deployment that sets `disabled`. The service raises VALIDATION_FAILED with `phone` in
 * `fieldErrors` when the environment demands one and none arrived, so the SPA renders the
 * message against the field either way.
 *
 * E.164 only: `PhoneInput` (packages/ui) emits exactly this, and a looser rule would send
 * a message to a number no carrier can route while billing us for the attempt.
 */
/**
 * A tenant's display name, as INPUT (signup, operator tenant create): the text policy's
 * `label` profile (handoff 0009) at the existing 120 code points. Enforced here, at the form,
 * so a bad name is refused with a policy code on `tenantName` — rather than surfacing later
 * as a refusal of the `<Tenant> HQ` location name derived from it (`provisionHqLocation`).
 */
export const tenantNameSchema = label({ max: 120 });
export const signupRequestSchema = z.object({
    email: z.string().email().max(320),
    name: z.string().trim().min(1).max(120),
    password: passwordSchema,
    tenantName: tenantNameSchema,
    /** OPTIONAL: when absent the server derives a unique slug from `tenantName` (`deriveTenantSlug`). */
    tenantSlug: tenantSlugSchema.optional(),
    phone: e164Schema.optional(),
    /**
     * The signing-up browser's OWN IANA zone (`Intl.DateTimeFormat().resolvedOptions().timeZone`),
     * used for exactly one thing: the timezone of the `<Tenant> HQ` location signup auto-creates
     * (S37 Slice A). Not stored on `Tenant` — a tenant has no timezone, a SITE does.
     *
     * OPTIONAL, AND THE SERVER DEFAULTS TO `UTC`. A signup from a client that cannot resolve a
     * zone, or from curl, must still produce a usable tenant; `UTC` is wrong-but-harmless and
     * the wizard's first step surfaces it for one-click confirmation anyway. A REQUIRED field
     * here would turn a browser quirk into a failed signup, which is the worst possible trade at
     * the top of the funnel.
     */
    timezone: ianaTimezoneSchema.optional(),
    /** The signing-up person agreed to the Terms of Service and Privacy Policy (`TERMS_VERSION`). */
    acceptTerms: z.literal(true),
    /** Bot-protection token from the form's captcha widget (issue #150). */
    captchaToken: captchaTokenSchema,
});
/**
 * WHAT A SUCCESSFUL SIGNUP RETURNS, AND WHAT IT DELIBERATELY DOES NOT.
 *
 * No token, no tenant id, no user id, and no code. The account is PENDING and cannot be
 * signed in to, so a token would be a lie; the ids are of no use to a caller who cannot
 * yet authenticate, and returning them would hand an abuser a confirmed handle for a row
 * they created. `channels` is the ONLY thing the UI needs: it decides whether to route to
 * `/verify-email` alone or to `/verify-email` then `/verify-phone`.
 */
export const signupResponseSchema = z.object({
    channels: z.array(verificationChannelSchema).min(1),
});
/**
 * `email` is carried on BOTH verify requests, and on the phone one it is the only thing that
 * says whose account this is. `VerificationCode` deliberately holds no `userId` and no
 * `tenantId` (see the model comment in schema.prisma: the redeem happens before any tenant is
 * known), so the phone code alone proves "somebody controls this number" and nothing more.
 * The email names the PENDING account the proof is being applied to.
 *
 * That is not a privilege: an attacker who supplies a stranger's email plus a code sent to
 * their own phone can mark that stranger's phone step complete, and can do nothing else —
 * activation still needs the EMAIL code, which only reaches the stranger's inbox. Recorded
 * here so the narrowness is deliberate rather than discovered.
 */
export const verifyEmailRequestSchema = z.object({
    email: z.string().email().max(320),
    code: z
        .string()
        .trim()
        .length(6)
        .regex(/^\d{6}$/),
});
export const verifyPhoneRequestSchema = z.object({
    email: z.string().email().max(320),
    phone: e164Schema,
    code: z
        .string()
        .trim()
        .length(6)
        .regex(/^\d{6}$/),
});
/**
 * `activated` is the ONE bit the SPA needs: false means "one more channel to go, route to the
 * next verify screen", true means "route to the sign-in form". No token, no tenant id, no
 * user id and no plan — the caller still has to sign in, which keeps this route from being a
 * token-minting surface that only needs an out-of-band code (the same property
 * `/auth/reset-password` holds).
 */
export const verifyResponseSchema = z.object({
    activated: z.boolean(),
    remainingChannels: z.array(verificationChannelSchema),
});
/* -------------------------------------------------------------------------- */
/* Resending a verification code — S26-TASK-021                                */
/* -------------------------------------------------------------------------- */
/**
 * The floor between two codes for one target, in seconds — the SERVER's rule, exported here
 * because the SPA needs the same number to render its cooldown timer.
 *
 * IT IS NOT A CLIENT-SIDE CONTROL. The server enforces the floor against
 * `VerificationCode.createdAt` on every request; the timer exists so a person is told why the
 * button is inert rather than pressing a button that silently does nothing. A caller that
 * ignores it gets a byte-identical 204 and no message. Stated in contracts rather than returned
 * in a response body because the response body IS the non-disclosure property — see the route
 * doc in identity/routes.ts.
 */
export const RESEND_COOLDOWN_SECONDS = 60;
/**
 * A DISCRIMINATED UNION AND ONE ROUTE, where verify-email/verify-phone are two routes — and the
 * asymmetry is deliberate. The two verify routes differ in what they RETURN (`remainingChannels`
 * routes the SPA to a different screen). Resend returns nothing at all, in every branch, on
 * purpose; splitting it in two would duplicate the eligibility ladder and the non-disclosure
 * reasoning across two handlers, which is how one of them eventually drifts and becomes an
 * oracle. One route, one bucket, one policy.
 *
 * `email` is required on BOTH arms, including SMS, and it is what names the account — exactly as
 * on `verifyPhoneRequestSchema`, and for the identical reason recorded there: `VerificationCode`
 * carries no `userId`, and `User` deliberately stores NO PHONE NUMBER (S26-TASK-012 stores
 * `phoneVerifiedAt`, a timestamp, precisely so that Constraint 6 holds). The server therefore
 * cannot look up "the number for this pending signup" — the caller must name it, and the code
 * goes to the number the caller named and to nothing else.
 *
 * There is deliberately NO `resendCodeResponseSchema`: a `204` has no body, exactly as
 * `/auth/forgot-password`.
 */
export const resendCodeRequestSchema = z.discriminatedUnion('channel', [
    z.object({
        channel: z.literal('EMAIL'),
        email: z.string().email().max(320),
        captchaToken: captchaTokenSchema,
    }),
    z.object({
        channel: z.literal('SMS'),
        email: z.string().email().max(320),
        phone: e164Schema,
        captchaToken: captchaTokenSchema,
    }),
]);
//# sourceMappingURL=signup.js.map